import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subscription, firstValueFrom, timeout } from 'rxjs';
import { TenantAuthService } from '../../core/services/tenant-auth.service';
import { InactivityTimeoutService } from '../../core/services/session/inactivity-timeout.service';
import { TenantNotification, TenantNotificationService } from '../../core/services/tenant-notification.service';
import { OrganizationContextItem, TenantPortalDashboard, TenantPortalLeaseOption, TenantPortalService } from '../../core/services/tenant-portal.service';
import { TenantProfileService, TenantUserProfile } from '../../core/services/tenant-profile.service';
import { WorkspaceBrandingService } from '../../core/services/workspace-branding.service';
import { presentPaymentNotice } from '../../core/services/tenant-payment-notice';
import { environment } from '../../../environments/environment';

type TenantThemePreference = 'light' | 'dark' | 'system';
type TenantHomeOption = TenantPortalLeaseOption & { selectionKey: string };
type TenantResolvedTheme = 'light' | 'dark';

const TENANT_THEME_STORAGE_KEY = 'apexTenantColorScheme';

@Component({
  selector: 'app-tenant-shell',
  imports: [RouterLink, RouterOutlet],
  templateUrl: './tenant-shell.html',
  styleUrl: './tenant-shell.scss',
})
export class TenantShell implements OnInit, OnDestroy {
  protected readonly branding = inject(WorkspaceBrandingService).branding;
  protected readonly dashboard = signal<TenantPortalDashboard | null>(null);
  protected readonly sessionMissing = signal(false);
  protected readonly workspaceUnavailable = signal(false);
  protected readonly portalLoadFailed = signal(false);
  protected readonly workspaceSelectionUrl = `${environment.publicAppUrl.replace(/\/+$/, '')}/select-workspace`;
  protected readonly recoveryMessage = computed(() => this.workspaceUnavailable()
    ? 'Your account could not access the selected workspace, lease or unit. Choose a workspace to continue, or contact your property team if access should be available.'
    : this.sessionMissing()
      ? 'We could not verify access to this page. You can sign in again or choose a workspace.'
      : 'We could not load your workspace right now. Try again, choose a workspace or sign out.');
  protected readonly signingOut = signal(false);
  protected readonly notifications = signal<TenantNotification[]>([]);
  protected readonly unreadNotificationCount = signal(0);
  protected readonly notificationsOpen = signal(false);
  protected readonly notificationsLoading = signal(false);
  protected readonly themeMode = signal<TenantResolvedTheme>('light');
  protected readonly profile = signal<TenantUserProfile | null>(null);
  protected readonly profileImageLoadFailed = signal(false);
  protected readonly workspaceMenuOpen = signal(false);
  protected readonly workspaceOptions = signal<OrganizationContextItem[]>([]);
  protected readonly activeWorkspaceId = signal<string | null>(null);
  protected readonly homeGroups = computed(() => {
    const groups = new Map<string, { label: string; items: TenantHomeOption[] }>();
    for (const option of this.dashboard()?.leaseOptions ?? []) {
      if (!option.leaseId && !option.unitId) continue;
      const orgId = option.orgId || this.dashboard()?.organization?.id || '';
      if (!groups.has(orgId)) groups.set(orgId, { label: option.orgName || 'Your property team', items: [] });
      groups.get(orgId)!.items.push({ ...option, selectionKey: this.homeKey(option) });
    }
    return [...groups.values()];
  });
  protected readonly selectedHomeKey = computed(() => {
    const options = this.dashboard()?.leaseOptions ?? [];
    const selected = options.find(option => option.selected)
      || options.find(option => !!option.leaseId && option.leaseId === this.dashboard()?.lease?.id);
    return selected ? this.homeKey(selected) : null;
  });
  private homeKey(option: TenantPortalLeaseOption): string {
    return `${option.orgId || this.dashboard()?.organization?.id || ''}:${option.leaseId || ''}:${option.unitId || ''}`;
  }
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly inactivityTimeout = inject(InactivityTimeoutService);
  private themePreference: TenantThemePreference = 'system';
  protected readonly navItems = [
    { label: 'Dashboard', icon: 'dashboard', link: '/dashboard' },
    { label: 'My Leases', icon: 'leases', link: '/leases' },
    { label: 'Rents & Payment', icon: 'rents-payment', link: '/rents-payment' },
    { label: 'Calendar', icon: 'calendar', link: '/calendar' },
    { label: 'Documents', icon: 'documents', link: '/documents' },
    { label: 'Inspections', icon: 'inspections', link: '/inspections' },
    { label: 'Maintenance', icon: 'maintenance', link: '/maintenance' },
    { label: 'Messages', icon: 'messages', link: '/communications' },
  ];
  private readonly brandingService = inject(WorkspaceBrandingService);
  private readonly tenantPortal = inject(TenantPortalService);
  private readonly tenantProfile = inject(TenantProfileService);
  private readonly tenantNotifications = inject(TenantNotificationService);
  private readonly auth = inject(TenantAuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly portalQueryParamKeys = ['workspace', 'org', 'orgId', 'leaseId', 'unitId'] as const;
  private readonly transientPaymentQueryParamKeys = [
    'view',
    'chargeId',
    'actionTitle',
    'actionDetail',
    'actionType',
    'actionAmount',
    'actionDueDate',
    'paymentAttemptId',
    'payment_intent',
    'payment_intent_client_secret',
    'redirect_status',
  ] as const;
  private notificationOrgId: string | null = null;
  private notificationEventSource: EventSource | null = null;
  private notificationReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private notificationReconnectAttempts = 0;
  private contextSubscription: Subscription | null = null;
  private dashboardVersion = 0;
  private profileSubscription: Subscription | null = null;

  constructor() {
    const params = this.route.snapshot.queryParamMap;
    this.brandingService.load(
      params.get('workspace') || params.get('org') || params.get('orgId'),
    );
    this.applyInitialTheme();
  }

  ngOnInit(): void {
    // The app-level start is skipped on /login, and signing in navigates here without a reload.
    this.inactivityTimeout.start();
    this.profileSubscription = this.tenantProfile.profile$.subscribe((profile) => {
      this.profile.set(profile);
      this.profileImageLoadFailed.set(false);
    });
    void this.loadWorkspaceOptions();
    this.contextSubscription = this.route.queryParamMap.subscribe(() => this.loadDashboard());
  }

  ngOnDestroy(): void {
    this.inactivityTimeout.stop();
    this.contextSubscription?.unsubscribe();
    this.profileSubscription?.unsubscribe();
    this.closeNotificationStream();
  }

  protected workspaceHomes(orgId: string): TenantHomeOption[] {
    return this.homeGroups().flatMap(group => group.items).filter(home => (home.orgId || this.dashboard()?.organization?.id) === orgId);
  }

  protected changeHome(selectionKey: string | null): void {
    const selected = this.homeGroups().flatMap(group => group.items).find(home => home.selectionKey === selectionKey);
    if (!selected) return;
    this.workspaceMenuOpen.set(false);
    if (selectionKey === this.selectedHomeKey()) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        orgId: selected?.orgId || null,
        leaseId: selected.leaseId || null,
        unitId: selected?.unitId || null,
        workspace: null,
        org: null,
        ...this.clearedTransientPaymentQueryParams(),
      },
      queryParamsHandling: 'merge',
    }).then(() => this.loadDashboard());
  }

  protected portalQueryParams(): Record<string, string> {
    const params = this.route.snapshot.queryParamMap;
    return this.portalQueryParamKeys.reduce<Record<string, string>>((accumulator, key) => {
      const value = params.get(key);
      if (value) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
  }

  protected navigateToNavItem(link: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    void this.router.navigateByUrl(this.navHref(link));
  }

  protected navHref(link: string): string {
    const normalized = link.startsWith('/') ? link : `/${link}`;
    return this.router.serializeUrl(this.router.createUrlTree([normalized], {
      queryParams: {
        ...this.portalQueryParams(),
        ...this.clearedTransientPaymentQueryParams(),
      },
    }));
  }

  protected isNavItemActive(link: string): boolean {
    const normalized = link.startsWith('/') ? link : `/${link}`;
    return this.router.isActive(this.router.createUrlTree([normalized]), {
      paths: normalized === '/dashboard' ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  protected leaseOptions(): TenantPortalLeaseOption[] {
    return this.dashboard()?.leaseOptions ?? [];
  }

  protected selectedLeaseId(): string {
    return this.dashboard()?.lease?.id || this.leaseOptions().find((lease) => lease.selected)?.leaseId || '';
  }

  protected selectedLeaseOption(): TenantPortalLeaseOption | null {
    const selectedId = this.selectedLeaseId();
    const currentUnitId = this.dashboard()?.lease?.unitId;
    return this.leaseOptions().find((lease) => lease.leaseId === selectedId)
      ?? this.leaseOptions().find((lease) => lease.unitId === currentUnitId)
      ?? this.leaseOptions().find((lease) => lease.selected)
      ?? this.leaseOptions()[0]
      ?? null;
  }

  protected leaseOptionLabel(option: TenantPortalLeaseOption): string {
    const property = option.propertyName?.trim() || 'Property pending';
    const unit = option.unitName?.trim() ? `Unit ${option.unitName}` : 'Unit pending';
    const org = option.orgName?.trim();
    return [property, unit, org].filter(Boolean).join(' · ');
  }

  protected leaseOptionContextLabel(option: TenantPortalLeaseOption | null | undefined): string {
    if (!option) return this.unitName();
    const unit = option.unitName?.trim() ? `Unit ${option.unitName}` : 'Unit pending';
    const org = option.orgName?.trim();
    return [unit, org].filter(Boolean).join(' · ');
  }

  protected leaseOptionImage(option: TenantPortalLeaseOption | null | undefined): string {
    return option?.propertyImageUrl
      || option?.coverImageUrl
      || option?.imageUrl
      || option?.propertyLogoUrl
      || this.branding().coverImageUrl
      || this.branding().logoUrl
      || '';
  }

  protected selectedPropertyImage(): string {
    const lease = this.dashboard()?.lease;
    return lease?.propertyImageUrl
      || lease?.coverImageUrl
      || lease?.imageUrl
      || this.leaseOptionImage(this.selectedLeaseOption());
  }

  private loadDashboard(): void {
    const version = ++this.dashboardVersion;
    this.clearLegacyMockPreference();
    const params = this.route.snapshot.queryParamMap;
    const orgId = this.uuidParam(params.get('orgId'));
    const leaseId = this.backendParam(params.get('leaseId'));
    const unitId = this.backendParam(params.get('unitId'));

    this.tenantPortal.dashboard(orgId, leaseId, unitId).pipe(timeout(12000)).subscribe({
      next: (dashboard) => {
        if (version !== this.dashboardVersion) return;
        this.brandingService.apply({
          orgId: dashboard.organization?.id,
          orgName: dashboard.organization?.name,
          logoUrl: dashboard.organization?.logoUrl,
          primaryColor: dashboard.organization?.primaryColor,
          currency: dashboard.organization?.currency,
        });
        this.dashboard.set(dashboard);
        this.syncSelectedPortalContext(dashboard);
        this.activeWorkspaceId.set(dashboard.organization?.id ?? this.route.snapshot.queryParamMap.get('orgId'));
        this.sessionMissing.set(false);
        this.workspaceUnavailable.set(false);
        this.portalLoadFailed.set(false);
        this.loadProfile();
        this.configureNotifications(dashboard.organization?.id);
        this.dismissInitialLoader();
      },
      error: (error) => {
        if (version !== this.dashboardVersion) return;
        this.dashboard.set(null);
        this.portalLoadFailed.set(true);
        this.workspaceUnavailable.set(this.isWorkspaceUnavailableError(error));
        this.sessionMissing.set((error?.status === 401 || error?.status === 403) && !this.workspaceUnavailable());
        this.dismissInitialLoader();
        this.closeNotificationStream();
      },
    });
  }

  private async loadWorkspaceOptions(): Promise<void> {
    try {
      const context = await firstValueFrom(this.tenantPortal.getOrganizationContext().pipe(timeout(8000)));
      const options = context.organizations ?? [];
      this.workspaceOptions.set(options);
      const requestedOrgId = this.uuidParam(this.route.snapshot.queryParamMap.get('orgId'));
      const active = requestedOrgId
        ?? options.find((organization) => organization.orgId === context.defaultOrgId)?.orgId
        ?? options[0]?.orgId
        ?? null;
      this.activeWorkspaceId.set(active);
    } catch {
      this.workspaceOptions.set([]);
    }
  }

  private isWorkspaceUnavailableError(error: any): boolean {
    const message = String(error?.error?.message || error?.message || '').toLowerCase();
    return error?.status === 403 && message.includes('tenant portal access denied');
  }

  private clearLegacyMockPreference(): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('tenant-payments-mock');
  }

  private backendParam(value: string | null): string | null {
    if (!value || value.startsWith('mock-')) return null;
    return value;
  }

  private uuidParam(value: string | null): string | null {
    const candidate = value?.trim();
    if (!candidate || candidate.startsWith('mock-')) return null;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidate)
      ? candidate
      : null;
  }

  private clearedTransientPaymentQueryParams(): Record<string, null> {
    return this.transientPaymentQueryParamKeys.reduce<Record<string, null>>((accumulator, key) => {
      accumulator[key] = null;
      return accumulator;
    }, {});
  }

  private syncSelectedPortalContext(dashboard: TenantPortalDashboard): void {
    const selected = this.selectedLeaseOptionFromDashboard(dashboard);
    const orgId = dashboard.organization?.id || selected?.orgId || null;
    const leaseId = dashboard.lease?.id || selected?.leaseId || null;
    const unitId = dashboard.lease?.unitId || selected?.unitId || null;
    const params = this.route.snapshot.queryParamMap;
    const currentOrgId = params.get('orgId');
    const currentLeaseId = params.get('leaseId');
    const currentUnitId = params.get('unitId');

    if ((orgId || null) === (currentOrgId || null)
      && (leaseId || null) === (currentLeaseId || null)
      && (unitId || null) === (currentUnitId || null)) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        orgId,
        leaseId,
        unitId,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private selectedLeaseOptionFromDashboard(dashboard: TenantPortalDashboard): TenantPortalLeaseOption | null {
    const leaseId = dashboard.lease?.id;
    const unitId = dashboard.lease?.unitId;
    const options = dashboard.leaseOptions ?? [];
    return options.find((option) => leaseId && option.leaseId === leaseId)
      ?? options.find((option) => unitId && option.unitId === unitId)
      ?? options.find((option) => option.selected)
      ?? options[0]
      ?? null;
  }

  private workspaceDashboardUrl(organization: OrganizationContextItem): string {
    const portalMode = String(organization.portalMode ?? '').toUpperCase();
    const landingPath = this.safeLandingPath(organization.landingPath);
    const baseUrl = portalMode === 'TENANT'
      ? environment.tenantAppUrl
      : portalMode === 'OWNER_PORTAL'
        ? environment.ownerAppUrl
        : environment.backofficeAppUrl;
    const url = new URL(`${this.portalBaseUrl(baseUrl, organization.portalSlug)}${landingPath}`);
    url.searchParams.set('orgId', organization.orgId);
    return url.toString();
  }

  private safeLandingPath(path?: string | null): string {
    const normalized = path?.trim();
    return normalized?.startsWith('/') ? normalized : '/dashboard';
  }

  private portalBaseUrl(baseUrl: string, portalSlug?: string | null): string {
    const slug = portalSlug?.trim().toLowerCase();
    if (!slug) {
      return baseUrl;
    }

    try {
      const url = new URL(baseUrl);
      if (url.hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname)) {
        return url.origin;
      }

      if (!url.hostname.startsWith(`${slug}.`)) {
        url.hostname = `${slug}.${url.hostname}`;
      }

      return url.origin;
    } catch {
      return baseUrl;
    }
  }

  private dismissInitialLoader(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const loader = document.getElementById('tenant-initial-loader');
    if (!loader) {
      return;
    }

    loader.style.opacity = '0';
    loader.style.pointerEvents = 'none';
    loader.style.transition = 'opacity 160ms ease';
    window.setTimeout(() => loader.remove(), 180);
  }

  protected retryPortal(): void {
    window.location.reload();
  }

  protected login(): void {
    this.auth.redirectToLogin('/dashboard');
  }

  protected canSwitchWorkspaces(): boolean {
    return this.workspaceOptions().length > 1 || this.homeGroups().flatMap(group => group.items).length > 1;
  }

  protected toggleWorkspaceMenu(): void {
    if (!this.canSwitchWorkspaces()) {
      return;
    }
    this.workspaceMenuOpen.set(!this.workspaceMenuOpen());
    this.notificationsOpen.set(false);
  }

  protected closeWorkspaceMenu(): void {
    this.workspaceMenuOpen.set(false);
  }

  protected switchToWorkspace(organization: OrganizationContextItem): void {
    if (organization.orgId === this.activeWorkspaceId()) {
      this.workspaceMenuOpen.set(false);
      return;
    }

    this.workspaceMenuOpen.set(false);
    window.location.href = this.workspaceDashboardUrl(organization);
  }

  protected workspaceInitials(name: string | null | undefined): string {
    return (name || 'Workspace')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'W';
  }

  protected workspaceRoleLabel(organization: OrganizationContextItem): string {
    const portalMode = String(organization.portalMode ?? '').toUpperCase();
    const role = String(organization.orgRole ?? '').trim().replace(/_/g, ' ').toLowerCase();
    const label = role ? role.replace(/\b\w/g, (match) => match.toUpperCase()) : 'Workspace';
    if (portalMode === 'TENANT') return 'Tenant Portal';
    if (portalMode === 'OWNER_PORTAL') return 'Owner Portal';
    if (portalMode === 'PROPERTY_MANAGER') return 'Property Manager';
    return label;
  }

  protected async logout(): Promise<void> {
    if (this.signingOut()) {
      return;
    }

    this.signingOut.set(true);
    try {
      await this.auth.logout();
    } finally {
      this.signingOut.set(false);
      this.auth.redirectToLogin('/dashboard');
    }
  }

  protected tenantName(): string {
    const profile = this.profile();
    const tenant = this.dashboard()?.tenant;
    const name = [profile?.firstName || tenant?.firstName, profile?.lastName || tenant?.lastName].filter(Boolean).join(' ').trim();
    return name || profile?.displayName || profile?.email || tenant?.email || 'Resident';
  }

  protected tenantInitials(): string {
    const profile = this.profile();
    const tenant = this.dashboard()?.tenant;
    const source = [profile?.firstName || tenant?.firstName, profile?.lastName || tenant?.lastName].filter(Boolean).join(' ').trim();
    const fallback = profile?.displayName?.trim()
      || this.emailInitialSource(profile?.email || tenant?.email)
      || this.tenantName();
    const parts = (source || fallback).split(/\s+/).filter(Boolean);
    const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');

    if (initials.length >= 2) {
      return initials;
    }

    const compact = (source || fallback).replace(/[^a-z0-9]/gi, '').toUpperCase();
    return compact.slice(0, 2) || 'RE';
  }

  protected tenantProfileImageUrl(): string {
    if (this.profileImageLoadFailed()) {
      return '';
    }
    const profile = this.profile();
    return profile?.profileImageUrl?.trim()
      || profile?.profilePictureUrl?.trim()
      || profile?.avatarUrl?.trim()
      || profile?.photoUrl?.trim()
      || this.dashboard()?.tenant?.profileImageUrl?.trim()
      || '';
  }

  protected onTenantProfileImageError(): void {
    this.profileImageLoadFailed.set(true);
  }

  protected propertyName(): string {
    return this.dashboard()?.lease?.propertyName?.trim() || this.branding().orgName || 'Current home';
  }

  protected unitName(): string {
    const unit = this.dashboard()?.lease?.unitName?.trim();
    return unit ? `Unit ${unit}` : 'Unit pending';
  }

  protected toggleNotifications(): void {
    this.notificationsOpen.set(!this.notificationsOpen());
    if (this.notificationsOpen()) {
      this.workspaceMenuOpen.set(false);
    }
    if (this.notificationsOpen()) {
      this.refreshNotifications();
    }
  }

  protected closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  @HostListener('document:pointerdown', ['$event'])
  protected closeNotificationsOnOutsideClick(event: PointerEvent): void {
    if (!this.notificationsOpen() && !this.workspaceMenuOpen()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    const menu = this.elementRef.nativeElement.querySelector('[data-tenant-notifications-menu]');
    if (this.notificationsOpen() && !menu?.contains(target)) {
      this.notificationsOpen.set(false);
    }

    const workspaceMenu = this.elementRef.nativeElement.querySelector('[data-tenant-workspace-menu]');
    if (this.workspaceMenuOpen() && !workspaceMenu?.contains(target)) this.workspaceMenuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  protected closeNotificationsOnEscape(): void {
    this.notificationsOpen.set(false);
    this.workspaceMenuOpen.set(false);
  }

  protected toggleTheme(): void {
    this.setTheme(this.themeMode() === 'dark' ? 'light' : 'dark');
  }

  protected themeButtonLabel(): string {
    return this.themeMode() === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  }

  protected markAllNotificationsRead(): void {
    if (!this.notificationOrgId) return;
    this.tenantNotifications.markAllRead(this.notificationOrgId).subscribe({
      next: () => {
        this.unreadNotificationCount.set(0);
        this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
      },
    });
  }

  protected openNotification(notification: TenantNotification): void {
    const orgId = this.notificationOrgId;
    if (orgId && !notification.read) {
      this.tenantNotifications.markRead(orgId, notification.id).subscribe({
        next: () => {
          this.notifications.update((items) =>
            items.map((item) => (item.id === notification.id ? { ...item, read: true } : item)),
          );
          this.unreadNotificationCount.update((count) => Math.max(0, count - 1));
        },
      });
    }

    this.notificationsOpen.set(false);
    if (notification.actionUrl) {
      this.openTenantUrl(notification.actionUrl);
    }
  }

  private openTenantUrl(url: string): void {
    url = url.trim();
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (/^[a-z][a-z\d+.-]*:/i.test(url) || url.startsWith('//')) return;

    const target = this.router.parseUrl(url.startsWith('/') ? url : `/${url}`);
    const context = this.portalQueryParams();
    const dashboard = this.dashboard();
    if (!context['orgId'] && dashboard?.organization?.id) context['orgId'] = dashboard.organization.id;
    if (context['orgId'] === dashboard?.organization?.id) {
      if (!context['leaseId'] && dashboard?.lease?.id) context['leaseId'] = dashboard.lease.id;
      if (!context['unitId'] && context['leaseId'] === dashboard?.lease?.id && dashboard?.lease?.unitId) {
        context['unitId'] = dashboard.lease.unitId;
      }
    }

    const explicit = target.queryParams;
    const changed = (key: string) => Object.hasOwn(explicit, key) && explicit[key] !== context[key];
    // Explicit destinations win; never attach the old lease/unit to a different workspace.
    if (['orgId', 'org', 'workspace'].some(changed)) {
      for (const key of this.portalQueryParamKeys) delete context[key];
    } else if (changed('leaseId')) {
      delete context['unitId'];
    } else if (changed('unitId')) {
      delete context['leaseId'];
    }
    target.queryParams = { ...context, ...explicit };
    void this.router.navigateByUrl(target);
  }

  protected notificationTimeLabel(notification: TenantNotification): string {
    const timestamp = Date.parse(notification.createdAt);
    if (Number.isNaN(timestamp)) return '';
    const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  protected notificationMessage(notification: TenantNotification): string {
    return this.readableStatusText(presentPaymentNotice({ ...notification, body: notification.message }, this.dashboard()?.organization?.currency).body || 'Review this update.');
  }

  protected notificationTitle(notification: TenantNotification): string {
    return presentPaymentNotice(notification).title;
  }

  protected notificationCountLabel(): string {
    const count = this.unreadNotificationCount();
    return count > 9 ? '9+' : String(count);
  }

  private configureNotifications(orgId: string | null | undefined): void {
    const resolvedOrgId = orgId?.trim();
    if (!resolvedOrgId || this.notificationOrgId === resolvedOrgId) {
      return;
    }

    this.notificationOrgId = resolvedOrgId;
    this.refreshNotifications();
    this.openNotificationStream(resolvedOrgId);
  }

  private readableStatusText(value: string): string {
    return value
      .replace(/\bIN_PROGRESS\b/g, 'In progress')
      .replace(/\bCANCELLED\b/g, 'Cancelled')
      .replace(/\bSCHEDULED\b/g, 'Scheduled')
      .replace(/\bRESOLVED\b/g, 'Resolved')
      .replace(/\bCLOSED\b/g, 'Closed')
      .replace(/\bOPEN\b/g, 'Open');
  }

  private refreshNotifications(): void {
    if (!this.notificationOrgId) return;
    this.notificationsLoading.set(true);
    this.tenantNotifications.list(this.notificationOrgId).subscribe({
      next: (page) => {
        this.notifications.set(page.content ?? []);
        this.notificationsLoading.set(false);
      },
      error: () => this.notificationsLoading.set(false),
    });
    this.tenantNotifications.unreadCount(this.notificationOrgId).subscribe({
      next: (response) => this.unreadNotificationCount.set(response.unreadCount ?? 0),
      error: () => this.unreadNotificationCount.set(0),
    });
  }

  private loadProfile(): void {
    this.tenantProfile.ensureProfile().catch(() => undefined);
  }

  private emailInitialSource(email: string | null | undefined): string {
    return email
      ?.trim()
      .split('@')[0]
      .split(/[._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .join(' ') || '';
  }

  private openNotificationStream(orgId: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.closeNotificationStream();
    this.notificationEventSource = new EventSource(this.tenantNotifications.streamUrl(orgId), {
      withCredentials: true,
    });

    this.notificationEventSource.addEventListener('connected', (event) => {
      this.applyUnreadCountEvent(event);
    });
    this.notificationEventSource.addEventListener('unread-count', (event) => {
      this.applyUnreadCountEvent(event);
    });
    this.notificationEventSource.addEventListener('notification', (event) => {
      const notification = this.parseEventData<TenantNotification>(event);
      if (!notification?.id) {
        return;
      }
      this.notifications.update((items) => [
        notification,
        ...items.filter((item) => item.id !== notification.id),
      ].slice(0, 10));
      if (!notification.read) {
        this.unreadNotificationCount.update((count) => count + 1);
      }
    });
    this.notificationEventSource.addEventListener('open', () => {
      this.notificationReconnectAttempts = 0;
    });
    this.notificationEventSource.onerror = () => {
      this.closeNotificationStream();
      this.scheduleNotificationReconnect(orgId);
    };
  }

  /** A dropped live feed (deploy, gateway timeout, network blip) reconnects with backoff and catches up on missed items. */
  private scheduleNotificationReconnect(orgId: string): void {
    if (this.notificationOrgId !== orgId) return;
    const delay = Math.min(60_000, 5_000 * 2 ** this.notificationReconnectAttempts);
    this.notificationReconnectAttempts += 1;
    this.notificationReconnectTimer = setTimeout(() => {
      this.notificationReconnectTimer = null;
      if (this.notificationOrgId !== orgId) return;
      this.refreshNotifications();
      this.openNotificationStream(orgId);
    }, delay);
  }

  private closeNotificationStream(): void {
    if (this.notificationReconnectTimer) {
      clearTimeout(this.notificationReconnectTimer);
      this.notificationReconnectTimer = null;
    }
    this.notificationEventSource?.close();
    this.notificationEventSource = null;
  }

  protected requestHelp(): void {
    void this.router.navigate(['/support'], { queryParams: this.portalQueryParams() });
  }

  private applyUnreadCountEvent(event: Event): void {
    const data = this.parseEventData<{ unreadCount?: number }>(event);
    if (typeof data?.unreadCount === 'number') {
      this.unreadNotificationCount.set(data.unreadCount);
    }
  }

  private parseEventData<T>(event: Event): T | null {
    try {
      const data = (event as MessageEvent).data;
      return data ? JSON.parse(data) as T : null;
    } catch {
      return null;
    }
  }

  private applyInitialTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }

    let storedTheme: string | null = null;
    if (typeof window !== 'undefined') {
      try {
        storedTheme = window.localStorage.getItem(TENANT_THEME_STORAGE_KEY);
      } catch {
        storedTheme = null;
      }
    }

    this.setTheme(this.normalizeThemePreference(storedTheme) || 'system', false);
  }

  private setTheme(theme: TenantThemePreference, persist = true): void {
    this.themePreference = theme;
    const resolved = this.resolveTheme(theme);
    this.themeMode.set(resolved);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolved);
      document.documentElement.setAttribute('data-theme-preference', theme);
      document.documentElement.style.setProperty('color-scheme', resolved);
      document.body.setAttribute('data-theme', resolved);
      document.body.setAttribute('data-theme-preference', theme);
    }
    if (persist && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(TENANT_THEME_STORAGE_KEY, theme);
      } catch {
        // Ignore storage failures so private browsing does not block the UI.
      }
    }
  }

  private normalizeThemePreference(value: string | null | undefined): TenantThemePreference | null {
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  }

  private resolveTheme(theme: TenantThemePreference): TenantResolvedTheme {
    if (theme !== 'system') return theme;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
